import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApi } from '../../core/api/client'
import { EXPECT_AGENT, EXPECT_STAFF, isAgentRole, isStaffRole, roleLabel } from '../../core/auth/roles'
import { RoleMismatchBanner, RoleMismatchIcon } from '../../core/auth/RoleHint'

// Stock Allocation: a staff member (SC / HQSM / FSA) allocates shop stock to a
// sales agent. Flow top-to-bottom: pick the staff identity → a shop → stock
// items → an agent → review → initiate. If we also hold the target agent's
// token, we poll their activity book and offer one-click accept.

const POLL_MS = 5000

interface Identity {
  id: string
  friendlyName: string
  phoneNumber: string
  environment: string
  expired: boolean
  role: string | null
  sub: string | null
}
interface Shop { id: string; name: string; shortName: string }
interface StockModel { modelId: string; modelName: string; brandName: string; category: string; count: number }
interface StockItem { serialNumber: string; receivedAt: string | null; ageLabel: string | null }
interface AgentResult { id: string; fullName: string; phoneNumber: string; status: string; maxStock: number | null; allocatedStock: number | null }
interface OutletProductType { id: string; name: string }
interface OutletTypesForItem { serialNumber: string; outletProductTypes: OutletProductType[] }
interface ActivityItem {
  allocationId: string; allocationType: string; stockItemId: string; serialNumber: string
  modelName: string; brandName: string; status: string; possibleAction: string
  expiresAtUtc: string | null; allocatedToId: string; allocatedToName: string; failureReason: string | null
}

type ReviewRow = { item: StockItem; types: OutletProductType[]; chosenId: string | null }

// The API matches these case-insensitively, so we do too: status is
// "PendingReceiver"/"PendingSupplier"/"Processing"; possibleAction is
// "acceptReject" or "none" (NOT the SCREAMING_CASE I first guessed).
const isPending = (s: string) => {
  const x = (s || '').toLowerCase()
  return x.startsWith('pending') || x === 'processing'
}
// Whether the held identity can act on this line. Respect an explicit "none",
// but otherwise allow acting on any pending item (lenient to casing/variants).
const canAct = (p: ActivityItem) => isPending(p.status) && p.possibleAction.toLowerCase() !== 'none'

// "2024-03-15 · 42d in stock" from the shop-received timestamp.
function stockAge(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const days = Math.max(0, Math.floor((Date.now() - t) / 86_400_000))
  return `${iso.slice(0, 10)} · ${days}d in stock`
}

export function StockView() {
  const api = useApi()

  const [error, setError] = useState<string | null>(null)
  const [action] = useState<'allocate'>('allocate') // Return / Reallocate slot in here later

  // ── identities (staff actor + agent-token matching)
  const [identities, setIdentities] = useState<Identity[]>([])
  const [staffTokenId, setStaffTokenId] = useState<string | null>(null)

  const refreshIdentities = useCallback(async () => {
    const rows = await api.get<Identity[]>('/m/stock/identities')
    setIdentities(rows)
    return rows
  }, [api])
  useEffect(() => { void refreshIdentities().catch((e) => setError(e instanceof Error ? e.message : String(e))) }, [refreshIdentities])

  // Default the staff actor to a live, staff-role identity (fall back to any live one).
  useEffect(() => {
    if (identities.length === 0) return
    if (staffTokenId && identities.some((i) => i.id === staffTokenId)) return
    const live = identities.filter((i) => !i.expired)
    const pick = live.find((i) => isStaffRole(i.role)) ?? live[0] ?? identities[0]
    setStaffTokenId(pick?.id ?? null)
  }, [identities, staffTokenId])

  const staff = useMemo(() => identities.find((i) => i.id === staffTokenId) ?? null, [identities, staffTokenId])
  // Expired identities stay usable — the backend auto-refreshes them on use, so we
  // gate only on having one selected and show expiry as a hint. A refresh that
  // genuinely can't happen surfaces inline as the existing error.
  const staffSelected = !!staff
  const staffExpired = !!staff && staff.expired

  // ── shop
  const [shops, setShops] = useState<Shop[] | null>(null)
  const [shopId, setShopId] = useState<string | null>(null)
  const shop = useMemo(() => shops?.find((s) => s.id === shopId) ?? null, [shops, shopId])

  // ── stock browse (model → items) + selection cart
  const [models, setModels] = useState<StockModel[] | null>(null)
  const [openModel, setOpenModel] = useState<string | null>(null)
  const [itemsByModel, setItemsByModel] = useState<Record<string, StockItem[]>>({})
  const [selected, setSelected] = useState<Map<string, StockItem>>(new Map())

  // ── agent
  const [agentSearch, setAgentSearch] = useState('')
  const [agents, setAgents] = useState<AgentResult[] | null>(null)
  const [agent, setAgent] = useState<AgentResult | null>(null)
  const [agentBusy, setAgentBusy] = useState(false)

  // ── review + allocation
  const [review, setReview] = useState<ReviewRow[] | null>(null)
  const [busy, setBusy] = useState<'shops' | 'models' | 'prepare' | 'allocate' | null>(null)
  const [allocationId, setAllocationId] = useState<string | null>(null)
  const [pending, setPending] = useState<ActivityItem[]>([])
  const [acceptBusy, setAcceptBusy] = useState<Record<string, boolean>>({})

  // ── agent inbox: accept/reject pending allocations for a held agent identity,
  // independent of having just initiated one here.
  const [inboxTokenId, setInboxTokenId] = useState<string | null>(null)
  const [inbox, setInbox] = useState<ActivityItem[] | null>(null)

  const staffTokenQuery = staffTokenId ? `tokenId=${staffTokenId}` : ''

  // The held agent token that matches the chosen agent (same env) — enables one-click accept.
  const agentIdentity = useMemo(() => {
    if (!agent || !staff) return null
    // Expired agent tokens still qualify — accept/poll auto-refresh them on use.
    return identities.find((i) => i.sub === agent.id && i.environment === staff.environment) ?? null
  }, [agent, staff, identities])

  function resetBelowShop() {
    setModels(null); setOpenModel(null); setItemsByModel({}); setSelected(new Map())
    setReview(null); setAllocationId(null); setPending([])
  }

  // ── shops: load when the staff actor changes
  const loadShops = useCallback(async () => {
    if (!staffTokenId) return
    setBusy('shops'); setError(null)
    try {
      const rows = await api.get<Shop[]>(`/m/stock/shops?${staffTokenQuery}`)
      setShops(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e)); setShops([])
    } finally { setBusy(null) }
  }, [api, staffTokenId, staffTokenQuery])

  useEffect(() => { setShops(null); setShopId(null); resetBelowShop(); if (staffSelected) void loadShops() }, [staffTokenId, staffSelected, loadShops])

  async function selectShop(id: string) {
    setShopId(id); resetBelowShop()
    setBusy('models')
    try {
      const rows = await api.get<StockModel[]>(`/m/stock/shops/${id}/models?${staffTokenQuery}`)
      setModels(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e)); setModels([])
    } finally { setBusy(null) }
  }

  async function toggleModel(modelId: string) {
    if (openModel === modelId) { setOpenModel(null); return }
    setOpenModel(modelId)
    if (!itemsByModel[modelId] && shopId) {
      try {
        const rows = await api.get<StockItem[]>(`/m/stock/shops/${shopId}/items?${staffTokenQuery}&modelId=${encodeURIComponent(modelId)}`)
        setItemsByModel((m) => ({ ...m, [modelId]: rows }))
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        setItemsByModel((m) => ({ ...m, [modelId]: [] }))
      }
    }
  }

  function toggleItem(item: StockItem) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(item.serialNumber)) next.delete(item.serialNumber)
      else next.set(item.serialNumber, item)
      return next
    })
    setReview(null) // selection changed — any prepared review is stale
  }

  async function searchAgents() {
    if (!agentSearch.trim() || !staffTokenId) return
    setAgentBusy(true); setError(null)
    try {
      const rows = await api.get<AgentResult[]>(`/m/stock/agents?${staffTokenQuery}&search=${encodeURIComponent(agentSearch.trim())}`)
      setAgents(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e)); setAgents([])
    } finally { setAgentBusy(false) }
  }

  const selectedItems = useMemo(() => [...selected.values()], [selected])
  const canPrepare = staffSelected && !!shopId && !!agent && selectedItems.length > 0

  // ── prepare: resolve outlet product types, default a pick per item
  async function prepare() {
    if (!agent || !staffTokenId) return
    setBusy('prepare'); setError(null); setReview(null)
    try {
      const resolved = await api.post<OutletTypesForItem[]>('/m/stock/outlet-types', {
        tokenId: staffTokenId, agentId: agent.id, serialNumbers: selectedItems.map((i) => i.serialNumber),
      })
      const bySerial = new Map(resolved.map((r) => [r.serialNumber, r.outletProductTypes]))
      setReview(selectedItems.map((item) => {
        const types = bySerial.get(item.serialNumber) ?? []
        return { item, types, chosenId: types[0]?.id ?? null }
      }))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally { setBusy(null) }
  }

  function setChosenType(serialNumber: string, typeId: string) {
    setReview((prev) => prev?.map((r) => (r.item.serialNumber === serialNumber ? { ...r, chosenId: typeId } : r)) ?? prev)
  }

  async function allocate() {
    if (!review || !agent || !shopId || !staffTokenId) return
    setBusy('allocate'); setError(null)
    try {
      const items = review.map((r) => ({
        serialNumber: r.item.serialNumber,
        outletProductTypeId: r.chosenId,
        outletProductTypeName: r.types.find((t) => t.id === r.chosenId)?.name ?? null,
      }))
      const res = await api.post<{ ok: boolean; allocationId: string; detail: string }>('/m/stock/allocate', {
        tokenId: staffTokenId, shopId, salesAgentId: agent.id, items,
      })
      setAllocationId(res.allocationId)
      setReview(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally { setBusy(null) }
  }

  // ── poll the agent's activity book (only if we hold their token)
  const settled = pending.length > 0 && pending.every((p) => !isPending(p.status))
  useEffect(() => {
    if (!allocationId || !agentIdentity) return
    let stop = false
    const poll = async () => {
      try {
        const rows = await api.get<ActivityItem[]>(`/m/stock/pending?tokenId=${agentIdentity.id}&status=All`, { quiet: true })
        if (!stop) setPending(rows.filter((r) => r.allocationId === allocationId))
      } catch { /* transient — keep polling */ }
    }
    void poll()
    if (settled) return () => { stop = true }
    const handle = setInterval(poll, POLL_MS)
    return () => { stop = true; clearInterval(handle) }
  }, [allocationId, agentIdentity, settled, api])

  async function acceptReject(p: ActivityItem, accept: boolean) {
    if (!agentIdentity) return
    setAcceptBusy((b) => ({ ...b, [p.stockItemId]: true }))
    try {
      await api.post(`/m/stock/${accept ? 'accept' : 'reject'}`, {
        tokenId: agentIdentity.id, allocationId: p.allocationId, stockItemId: p.stockItemId,
      })
      const rows = await api.get<ActivityItem[]>(`/m/stock/pending?tokenId=${agentIdentity.id}&status=All`, { quiet: true })
      setPending(rows.filter((r) => r.allocationId === allocationId))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setAcceptBusy((b) => ({ ...b, [p.stockItemId]: false }))
    }
  }

  function newAllocation() {
    setAllocationId(null); setPending([]); setReview(null); setSelected(new Map())
  }

  // ── agent inbox
  const loadInbox = useCallback(async (quiet = false) => {
    if (!inboxTokenId) return
    try {
      const rows = await api.get<ActivityItem[]>(`/m/stock/pending?tokenId=${inboxTokenId}&status=All`, { quiet })
      setInbox(rows)
    } catch (e) {
      if (!quiet) setError(e instanceof Error ? e.message : String(e))
    }
  }, [api, inboxTokenId])

  // Default the inbox to a live agent identity (prefer a SalesAgent role).
  useEffect(() => {
    if (identities.length === 0 || inboxTokenId) return
    const live = identities.filter((i) => !i.expired)
    const pickAgent = live.find((i) => isAgentRole(i.role)) ?? live[0] ?? identities[0]
    setInboxTokenId(pickAgent?.id ?? null)
  }, [identities, inboxTokenId])

  useEffect(() => { setInbox(null); if (inboxTokenId) void loadInbox() }, [inboxTokenId, loadInbox])
  useEffect(() => {
    if (!inboxTokenId) return
    const h = setInterval(() => void loadInbox(true), POLL_MS)
    return () => clearInterval(h)
  }, [inboxTokenId, loadInbox])

  async function inboxAct(p: ActivityItem, accept: boolean) {
    if (!inboxTokenId) return
    setAcceptBusy((b) => ({ ...b, [p.stockItemId]: true }))
    try {
      await api.post(`/m/stock/${accept ? 'accept' : 'reject'}`, { tokenId: inboxTokenId, allocationId: p.allocationId, stockItemId: p.stockItemId })
      await loadInbox(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setAcceptBusy((b) => ({ ...b, [p.stockItemId]: false }))
    }
  }

  return (
    <div className="module">
      <h2>Stock Allocation</h2>
      <p className="muted">Allocate stock from a shop you manage to a sales agent. If you also hold the agent’s token, accept it right here.</p>

      {error && <div className="banner error">{error}</div>}

      {/* Action switch — Allocate now; Return / Reallocate are future siblings. */}
      <section>
        <h3>Action</h3>
        <div className="seg" role="group" aria-label="Stock action">
          <button type="button" className={`seg-btn ${action === 'allocate' ? 'on' : ''}`} aria-pressed>Allocate</button>
          <button type="button" className="seg-btn" disabled title="Coming later">Return</button>
          <button type="button" className="seg-btn" disabled title="Coming later">Reallocate</button>
        </div>
      </section>

      {/* Staff identity */}
      <section>
        <h3>Acting as (staff)</h3>
        {identities.length === 0 ? (
          <p className="muted">No tokens yet — mint a Stock Controller / HQSM token in <strong>Token Minter</strong> first.</p>
        ) : (
          <label className="field">
            <span className="field-label">Identity</span>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <select value={staffTokenId ?? ''} onChange={(e) => setStaffTokenId(e.target.value || null)}>
                {identities.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.friendlyName} · {roleLabel(i.role)} · {i.environment} · {i.expired ? 'expired' : 'live'}
                  </option>
                ))}
              </select>
              <RoleMismatchIcon exp={EXPECT_STAFF} role={staff?.role} />
            </div>
          </label>
        )}
        {staff && <RoleMismatchBanner exp={EXPECT_STAFF} role={staff.role} />}
        {staffExpired && (
          <div className="banner warning" style={{ marginTop: 12, marginBottom: 0 }}>Selected identity is expired — it’ll auto-refresh when you run an action (or refresh it in Token Minter).</div>
        )}
      </section>

      {/* Shop */}
      <section>
        <div className="row spread" style={{ alignItems: 'baseline' }}>
          <h3 style={{ border: 0, margin: 0, padding: 0 }}>Shop</h3>
          {staffSelected && <button className="linklike" onClick={loadShops}>reload</button>}
        </div>
        {!staffSelected && <p className="muted" style={{ marginTop: 14 }}>Select a staff identity to load shops.</p>}
        {staffSelected && busy === 'shops' && <p className="muted" style={{ marginTop: 14 }}>Loading shops…</p>}
        {staffSelected && shops?.length === 0 && busy !== 'shops' && <p className="muted" style={{ marginTop: 14 }}>No shops for this identity.</p>}
        {shops && shops.length > 0 && (
          <div className="chip-pick" style={{ marginTop: 12 }}>
            {shops.map((s) => (
              <button key={s.id} className={`pick ${shopId === s.id ? 'on' : ''}`} onClick={() => selectShop(s.id)}>
                {s.name}{s.shortName ? <span className="muted"> · {s.shortName}</span> : null}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Stock browse */}
      {shop && (
        <section>
          <div className="row spread" style={{ alignItems: 'baseline' }}>
            <h3 style={{ border: 0, margin: 0, padding: 0 }}>Stock in {shop.name}</h3>
            <span className="muted reg-autonote">{selected.size} selected</span>
          </div>
          {busy === 'models' && <p className="muted" style={{ marginTop: 14 }}>Loading models…</p>}
          {models?.length === 0 && busy !== 'models' && <p className="muted" style={{ marginTop: 14 }}>No stock in this shop.</p>}
          {models && models.length > 0 && (
            <ul className="reg-list">
              {models.map((m) => {
                const open = openModel === m.modelId
                const items = itemsByModel[m.modelId]
                return (
                  <li key={m.modelId} className="reg">
                    <button className="reg-head model-head" onClick={() => toggleModel(m.modelId)} aria-expanded={open}>
                      <span className="reg-caret">{open ? '▾' : '▸'}</span>
                      <span className="reg-name">{m.brandName} {m.modelName}</span>
                      <span className="reg-phone">{m.category}</span>
                      <span className="reg-pct">{m.count} in stock</span>
                    </button>
                    {open && (
                      <div className="reg-detail">
                        {!items && <p className="muted">Loading items…</p>}
                        {items?.length === 0 && <p className="muted">No items.</p>}
                        {items && items.length > 0 && (
                          <ul className="pick-list">
                            {items.map((it) => {
                              const on = selected.has(it.serialNumber)
                              return (
                                <li key={it.serialNumber}>
                                  <label className="pick-row">
                                    <input type="checkbox" checked={on} onChange={() => toggleItem(it)} />
                                    <code>{it.serialNumber}</code>
                                    {it.ageLabel && <span className="tag expired">{it.ageLabel}</span>}
                                    {it.receivedAt && <span className="pick-meta">{stockAge(it.receivedAt)}</span>}
                                  </label>
                                </li>
                              )
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* Agent */}
      {shop && (
        <section>
          <h3>Allocate to agent</h3>
          <div className="create-bar">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">Search</span>
              <input
                value={agentSearch}
                placeholder="name or phone"
                onChange={(e) => setAgentSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void searchAgents() }}
              />
            </label>
            <button disabled={!staffSelected || agentBusy || !agentSearch.trim()} onClick={searchAgents}>{agentBusy ? 'Searching…' : 'Search'}</button>
          </div>
          {agents && agents.length === 0 && <p className="muted" style={{ marginTop: 10 }}>No agents matched.</p>}
          {agents && agents.length > 0 && (
            <ul className="pick-list" style={{ marginTop: 12 }}>
              {agents.map((a) => {
                const on = agent?.id === a.id
                const held = identities.some((i) => i.sub === a.id && i.environment === staff?.environment)
                return (
                  <li key={a.id}>
                    <button className={`agent-row ${on ? 'on' : ''}`} onClick={() => setAgent(a)}>
                      <span className="ar-name">{a.fullName || '—'}</span>
                      <span className="ar-phone">{a.phoneNumber}</span>
                      <span className="ar-limit">{a.allocatedStock ?? '?'}/{a.maxStock ?? '?'} stock</span>
                      {held && <span className="tag live">token held</span>}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* Review + initiate */}
      {shop && !allocationId && (
        <section>
          <h3>Review &amp; initiate</h3>
          <p className="muted" style={{ marginTop: -6 }}>
            {agent ? <>To <strong>{agent.fullName}</strong> · {selectedItems.length} item(s) from <strong>{shop.name}</strong></> : 'Select an agent and at least one item.'}
          </p>
          {!review && (
            <button disabled={!canPrepare || busy === 'prepare'} onClick={prepare}>
              {busy === 'prepare' ? 'Resolving product types…' : 'Review allocation'}
            </button>
          )}
          {review && (
            <>
              <table className="steps">
                <tbody>
                  {review.map((r) => (
                    <tr key={r.item.serialNumber}>
                      <td><code>{r.item.serialNumber}</code></td>
                      <td>
                        {r.types.length === 0 ? (
                          <span className="muted">no product type</span>
                        ) : r.types.length === 1 ? (
                          <span className="muted">{r.types[0].name}</span>
                        ) : (
                          <select value={r.chosenId ?? ''} onChange={(e) => setChosenType(r.item.serialNumber, e.target.value)}>
                            {r.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="reg-actions" style={{ marginTop: 14 }}>
                <button disabled={busy === 'allocate'} onClick={allocate}>{busy === 'allocate' ? 'Initiating…' : `Allocate ${review.length} item(s)`}</button>
                <button className="ghost" disabled={busy === 'allocate'} onClick={() => setReview(null)}>Back</button>
              </div>
            </>
          )}
        </section>
      )}

      {/* Allocation tracker */}
      {allocationId && (
        <section>
          <div className="row spread" style={{ alignItems: 'baseline' }}>
            <h3 style={{ border: 0, margin: 0, padding: 0 }}>Allocation</h3>
            <button className="linklike" onClick={newAllocation}>new allocation</button>
          </div>
          <div className="banner success" style={{ marginTop: 12 }}>
            Requested <code>{allocationId}</code>{agent ? <> → {agent.fullName}</> : null}
          </div>

          {!agentIdentity && (
            <p className="muted">You don’t hold a live token for this agent — they’ll see the request in their app. Accept it there (5-minute window).</p>
          )}

          {agentIdentity && pending.length === 0 && (
            <p className="muted">Waiting for the request to appear in {agent?.fullName}’s activity book… (polling every {POLL_MS / 1000}s)</p>
          )}

          {agentIdentity && pending.length > 0 && (
            <ul className="pick-list" style={{ marginTop: 4 }}>
              {pending.map((p) => (
                <li key={p.stockItemId}>
                  <div className="alloc-row">
                    <code>{p.serialNumber}</code>
                    <span className="muted">{p.brandName} {p.modelName}</span>
                    <StatusPill status={p.status} />
                    {isPending(p.status) && p.expiresAtUtc && <Expiry iso={p.expiresAtUtc} />}
                    {canAct(p) && (
                      <span className="row" style={{ marginLeft: 'auto', gap: 8 }}>
                        <button disabled={acceptBusy[p.stockItemId]} onClick={() => acceptReject(p, true)}>{acceptBusy[p.stockItemId] ? '…' : 'Accept'}</button>
                        <button className="ghost" disabled={acceptBusy[p.stockItemId]} onClick={() => acceptReject(p, false)}>Reject</button>
                      </span>
                    )}
                    {p.failureReason && <span className="muted">{p.failureReason}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {agentIdentity && pending.some(canAct) && (
            <div className="reg-actions" style={{ marginTop: 12 }}>
              <button onClick={() => pending.filter(canAct).forEach((p) => void acceptReject(p, true))}>Accept all</button>
            </div>
          )}
        </section>
      )}

      {/* Agent inbox — accept/reject as any held agent identity */}
      <section>
        <div className="row spread" style={{ alignItems: 'baseline' }}>
          <h3 style={{ border: 0, margin: 0, padding: 0 }}>Incoming (accept as agent)</h3>
          {inboxTokenId && <button className="linklike" onClick={() => loadInbox()}>refresh</button>}
        </div>
        <p className="muted" style={{ marginTop: -6 }}>Pick one of your agent identities to see and accept its pending allocations — including ones initiated elsewhere.</p>
        {identities.length === 0 ? (
          <p className="muted">No tokens yet — mint an agent token in <strong>Token Minter</strong>.</p>
        ) : (
          <label className="field">
            <span className="field-label">Agent</span>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <select value={inboxTokenId ?? ''} onChange={(e) => setInboxTokenId(e.target.value || null)}>
                {identities.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.friendlyName} · {roleLabel(i.role)} · {i.environment} · {i.expired ? 'expired' : 'live'}
                  </option>
                ))}
              </select>
              <RoleMismatchIcon exp={EXPECT_AGENT} role={identities.find((i) => i.id === inboxTokenId)?.role} />
            </div>
          </label>
        )}
        {inboxTokenId && <RoleMismatchBanner exp={EXPECT_AGENT} role={identities.find((i) => i.id === inboxTokenId)?.role} />}
        {inbox && inbox.length === 0 && <p className="muted" style={{ marginTop: 12 }}>Nothing in this agent’s activity book.</p>}
        {inbox && inbox.length > 0 && (
          <ul className="pick-list" style={{ marginTop: 12 }}>
            {inbox.map((p) => (
              <li key={`${p.allocationId}:${p.stockItemId}`}>
                <div className="alloc-row">
                  <code>{p.serialNumber}</code>
                  <span className="muted">{p.brandName} {p.modelName}</span>
                  <StatusPill status={p.status} />
                  {isPending(p.status) && p.expiresAtUtc && <Expiry iso={p.expiresAtUtc} />}
                  {canAct(p) && (
                    <span className="row" style={{ marginLeft: 'auto', gap: 8 }}>
                      <button disabled={acceptBusy[p.stockItemId]} onClick={() => inboxAct(p, true)}>{acceptBusy[p.stockItemId] ? '…' : 'Accept'}</button>
                      <button className="ghost" disabled={acceptBusy[p.stockItemId]} onClick={() => inboxAct(p, false)}>Reject</button>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const cls = status === 'Allocated' ? 'on' : status === 'Failed' || status === 'Rejected' ? 'bad' : 'pend'
  return <span className={`status-pill ${cls}`}>{status}</span>
}

// Counts down the 5-minute acceptance window.
function Expiry({ iso }: { iso: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => { const h = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(h) }, [])
  const ms = new Date(iso).getTime() - now
  if (ms <= 0) return <span className="status-pill bad">expired</span>
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000)
  return <span className="muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{m}:{String(s).padStart(2, '0')}</span>
}
